import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import { webvonkClient } from './lib/supabase';
import { PermissionsProvider } from './kmfc-shop/hooks/usePermissions';
import { SessionProvider, useSession } from './contexts/SessionContext';
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext';
import { theme } from './styles/theme';
import LoginScreen from './login/LoginScreen';
import MainTabs from './kmfc-shop/navigation/MainTabs';
import KoffieApp from './koffiereparatie-geleen/KoffieApp';
import WhenconnectApp from './whenconnect/WhenconnectApp';
import CompanySelect from './company-select/CompanySelect';
import LogsPage from './logs/LogsPage';
import PinLockScreen from './components/PinLockScreen';
import { usePinLock } from './hooks/usePinLock';
import NotificationService from './kmfc-shop/services/NotificationService';
import OfflineIndicator from './components/OfflineIndicator';
import UpdateModal from './components/UpdateModal';
import WhatsNewModal from './components/WhatsNewModal';
import KoffieWhatsNewModal from './components/KoffieWhatsNewModal';
import VersionGuard from './components/VersionGuard';
import { SystemStatusProvider } from './kmfc-shop/components/SystemStatusProvider';
import packageJson from '../package.json';

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Futura PT';
    src: url('/fonts/FuturaCyrillicBook.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Futura PT';
    src: url('/fonts/FuturaCyrillicLight.ttf') format('truetype');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Futura PT';
    src: url('/fonts/FuturaCyrillicMedium.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Futura PT';
    src: url('/fonts/FuturaCyrillicDemi.ttf') format('truetype');
    font-weight: 600;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Futura PT';
    src: url('/fonts/FuturaCyrillicBold.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Futura PT';
    src: url('/fonts/FuturaCyrillicExtraBold.ttf') format('truetype');
    font-weight: 800;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Futura PT';
    src: url('/fonts/FuturaCyrillicHeavy.ttf') format('truetype');
    font-weight: 900;
    font-style: normal;
    font-display: swap;
  }
  body, html, * {
    font-family: 'Futura PT', sans-serif;
  }
  body {
    background: #000;
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 40px 32px;
  text-align: center;
  font-family: inherit;
`;

const MobileIcon = styled.div`
  font-size: 48px;
  margin-bottom: 24px;
`;

const MobileTitle = styled.h1`
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.03em;
  margin: 0 0 10px;
`;

const MobileSub = styled.p`
  font-size: 0.88rem;
  color: rgba(255,255,255,0.4);
  line-height: 1.55;
  margin: 0;
  max-width: 280px;
`;

const MobileBlocker = () => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const isKmfc = location.pathname.startsWith('/KMFC');
  if (!isMobile || !isKmfc) return null; // TIJDELIJK UITGESCHAKELD
  return null; // TODO: verwijder deze regel om blokkering terug te zetten

  return (
    <MobileOverlay>
      <MobileIcon>📱</MobileIcon>
      <MobileTitle>Download de app</MobileTitle>
      <MobileSub>
        Download de app om het dashboard op een telefoon te bekijken.
      </MobileSub>
    </MobileOverlay>
  );
};

function AuthWrapper({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, isReady, isAuthenticated, session } = useSession();

  useEffect(() => {
    if (!isReady) return;
    console.log('🔄 AuthWrapper effect:', { isReady, isAuthenticated, pathname: location.pathname, user: user ? 'exists' : 'null', session: session ? 'exists' : 'null', sessionData: session });
    if (isAuthenticated) {
      NotificationService.getInstance();
    } else {
      if (NotificationService.instance) NotificationService.instance.cleanup();
    }
    if (location.pathname.startsWith('/Koffiereparatie')) return;
    if (location.pathname.startsWith('/Whenconnect')) return;
    if (location.pathname.startsWith('/company-select')) return;
    if (location.pathname.startsWith('/logs')) return;
    if (isAuthenticated && (location.pathname === '/' || location.pathname === '/login')) {
      navigate('/KMFC/dashboard', { replace: true });
    } else if (!isAuthenticated && location.pathname.startsWith('/KMFC')) {
      navigate('/', { replace: true });
    }
  }, [isReady, isAuthenticated, location.pathname, navigate, user]);

  useEffect(() => {
    if (!isAuthenticated && isReady && !location.pathname.startsWith('/Koffiereparatie') && !location.pathname.startsWith('/Whenconnect') && !location.pathname.startsWith('/company-select') && !location.pathname.startsWith('/logs')) {
      const handlePopState = (event) => {
        const currentPath = window.location.pathname;
        if (currentPath !== '/' && currentPath !== '/login' && !currentPath.startsWith('/Koffiereparatie') && !currentPath.startsWith('/Whenconnect') && !currentPath.startsWith('/company-select') && !currentPath.startsWith('/logs')) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          window.history.replaceState(null, '', '/');
          navigate('/', { replace: true });
          return false;
        }
      };
      const handleBeforeUnload = () => {
        if (!isAuthenticated && !window.location.hash.startsWith('#/logs')) window.history.replaceState(null, '', '/');
      };
      window.addEventListener('popstate', handlePopState, true);
      window.addEventListener('beforeunload', handleBeforeUnload);
      if (location.pathname !== '/login' && !location.pathname.startsWith('/logs')) window.history.pushState({ page: 'welcome' }, '', '/');
      return () => {
        window.removeEventListener('popstate', handlePopState, true);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isAuthenticated, isReady, navigate, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated && isReady && location.pathname !== '/' && location.pathname !== '/login' && !location.pathname.startsWith('/Koffiereparatie') && !location.pathname.startsWith('/Whenconnect') && !location.pathname.startsWith('/company-select') && !location.pathname.startsWith('/logs')) {
      navigate('/', { replace: true });
    }
  }, [location.pathname, isAuthenticated, isReady, navigate]);

  useEffect(() => {
    if (!isReady) return;
    const securityCheck = setInterval(() => {
      if (!isAuthenticated && window.location.pathname !== '/' && window.location.pathname !== '/login' && !window.location.pathname.startsWith('/Koffiereparatie') && !window.location.pathname.startsWith('/Whenconnect') && !window.location.pathname.startsWith('/company-select') && !window.location.hash.startsWith('#/logs')) {
        window.history.replaceState(null, '', '/');
        window.location.href = '/';
      }
    }, 1000);
    return () => clearInterval(securityCheck);
  }, [isAuthenticated, isReady]);

  const { hasPin, locked, loading: pinLoading, unlock } = usePinLock();

  if (loading || !isReady || (isAuthenticated && pinLoading)) {
    const savedTheme = localStorage.getItem('kmfc-theme');
    const isLight = savedTheme === 'light' || (savedTheme !== 'dark' && window.matchMedia('(prefers-color-scheme: light)').matches);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: isLight ? '#f5f5f5' : '#0a0a0a' }}>
      </div>
    );
  }

  return (
    <>
      {isAuthenticated && hasPin && locked && (
        <ThemeProvider theme={theme}>
          <PinLockScreen onUnlock={unlock} />
        </ThemeProvider>
      )}
      {children({ user })}
    </>
  );
}

function KmfcWhatsNew() {
  const { user } = useSession();
  if (user?.company_slug !== 'kmfc-shop') return null;
  return <WhatsNewModal />;
}

function KoffieWhatsNew() {
  try {
    const session = JSON.parse(localStorage.getItem('koffie_session') || 'null');
    if (!session) return null;
  } catch { return null; }
  return <KoffieWhatsNewModal />;
}

function App() {
  const [updateInfo, setUpdateInfo] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const currentVersion = `V${packageJson.version}`;

  useEffect(() => { checkForUpdates(); }, []);

  const checkForUpdates = async () => {
    try {
      const { data, error } = await webvonkClient.from('updates').select('*').order('release', { ascending: false }).limit(1).single();
      if (error || !data) return;
      if (data.required) {
        const normalizedLatest = data.id.startsWith('V') ? data.id : `V${data.id}`;
        if (currentVersion !== normalizedLatest) {
          setUpdateInfo(data);
          setShowUpdateModal(true);
        }
      }
    } catch (error) {
      console.error('Error in update check:', error);
    }
  };

  return (
    <Router>
      <GlobalStyle />
      <MobileBlocker />
      <SessionProvider>
        <AuthWrapper>
          {({ user }) => (
            <Routes>
              <Route path="/logs" element={<LogsPage />} />
              <Route path="/Koffiereparatie/*" element={<VersionGuard><KoffieApp /></VersionGuard>} />
              <Route path="/Whenconnect/*" element={<VersionGuard><WhenconnectApp /></VersionGuard>} />
              <Route path="/company-select" element={<CompanySelect />} />
              {!user ? (
                <>
                  <Route path="/" element={<LoginScreen />} />
                  <Route path="/login" element={<LoginScreen />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </>
              ) : (
                <>
                  <Route path="/KMFC/*" element={
                    <VersionGuard>
                      <ThemeProvider theme={theme}>
                        <CustomThemeProvider>
                          <SystemStatusProvider>
                            <PermissionsProvider>
                              <MainTabs />
                            </PermissionsProvider>
                          </SystemStatusProvider>
                        </CustomThemeProvider>
                      </ThemeProvider>
                    </VersionGuard>
                  } />
                  <Route path="*" element={<Navigate to="/KMFC/dashboard" replace />} />
                </>
              )}
            </Routes>
          )}
        </AuthWrapper>
        <KmfcWhatsNew />
        <KoffieWhatsNew />
      </SessionProvider>
      <OfflineIndicator />
      {showUpdateModal && updateInfo && (
        <UpdateModal updateInfo={updateInfo} currentVersion={currentVersion} />
      )}
    </Router>
  );
}


export default App;